/* oxlint-disable typescript/no-non-null-assertion */
import { NetworkManagerError } from 'register-partners-js/services/network-manager';

export const NETWORK_ERROR_MESSAGES: string[] = [
  // Chrome & Edge
  'Failed to fetch',
  // Firefox
  'NetworkError when attempting to fetch resource.',
  // Safari
  'cancelled',
  'annulé',
  'Load failed',
  'La requête a expiré.',
  'La connexion Internet semble interrompue.',
  // German / French localised Safari & iOS messages
  'Abgebrochen',
  'La connexion réseau a été perdue.',
];

export class ErrorInfo {
  private error: unknown;

  /** @public */
  static for(error: unknown): ErrorInfo {
    return new ErrorInfo(error);
  }

  /** @private */
  constructor(error: unknown) {
    this.error = error;
  }

  /**
   * Returns the HTTP status code as a number, or `undefined` if this is not
   * an HTTP error.
   */
  get httpStatus(): number | undefined {
    if (this.isHttpError) {
      // oxlint-disable-next-line typescript/consistent-type-assertions
      return (this.error as NetworkManagerError).status;
    }
    return undefined;
  }

  /**
   * If an HTTP request responds with a non-2xx status code then this property will be `true`.
   */
  get isHttpError(): boolean {
    return this.error instanceof NetworkManagerError;
  }

  /**
   * If an HTTP request responds with a 4xx status code then this property will be `true`.
   */
  get isHttpClientError(): boolean {
    const { httpStatus } = this;
    return Boolean(httpStatus) && httpStatus! >= 400 && httpStatus! < 500;
  }

  /**
   * If an HTTP request responds with a 5xx status code then this property will be `true`.
   */
  get isHttpServerError(): boolean {
    const { httpStatus } = this;
    return Boolean(httpStatus) && httpStatus! >= 500 && httpStatus! < 600;
  }

  /**
   * If a network request is aborted then this property will be `true`.
   */
  get isAbortError(): boolean {
    return this.error instanceof DOMException && this.error.name === 'AbortError';
  }

  /**
   * If a network request fails due to network issues then this property will be `true`.
   */
  get isNetworkError(): boolean {
    return this.error instanceof TypeError && NETWORK_ERROR_MESSAGES.includes(this.error.message);
  }

  /**
   * If an error should be sent to Sentry then this property will be `true`.
   * We return `false` for network issues, request abortions and HTTP server errors.
   */
  get shouldSendToSentry(): boolean {
    return !this.isHttpServerError && !this.isAbortError && !this.isNetworkError;
  }
}
